import type { ArtifactPreview } from "~/services/artifacts";
import { AttachmentCard } from "./AttachmentCard";
import { useConversationContext } from "./ConversationContext";
import { ImageView } from "./ImageView";
import { useArtifactPreviewById } from "./artifacts/useArtifactPreview";

function imageUrl(content: ArtifactPreview["content"]): string | undefined {
  if (!content || typeof content !== "object" || Array.isArray(content)) return undefined;
  const url = (content as Record<string, unknown>).url;
  return typeof url === "string" ? url : undefined;
}

// Renders one attachment on a sent user bubble, resolved by artifact id via the
// preview endpoint. Images render through the shared ImageView (rounded image +
// lightbox) so uploads and agent-returned image artifacts look identical;
// everything else is the compact AttachmentCard the composer uses. Serves both
// the live turn and reload.
export const UserAttachment = ({ id }: { id: string }) => {
  const { conversationId, shareToken } = useConversationContext();
  const { preview, isLoading } = useArtifactPreviewById({ conversationId, shareToken }, id);

  if (isLoading) {
    return (
      <div className="h-16 w-52 animate-pulse rounded-lg bg-muted/60" aria-label="Attachment" />
    );
  }

  // Unresolved (deleted / preview error): still show a neutral file card so the
  // attachment doesn't silently vanish.
  if (!preview) {
    return <AttachmentCard filename="Attachment" />;
  }

  const filename = preview.filename ?? preview.artifact_name ?? "Attachment";
  const src = preview.artifact_type === "image" ? imageUrl(preview.content) : undefined;
  if (src) {
    return (
      <ImageView
        url={src}
        filename={preview.filename}
        artifactName={preview.artifact_name}
        summary={preview.summary}
        variant="thumbnail"
      />
    );
  }
  return <AttachmentCard filename={filename} />;
};
